/**
 * SF-4758: product priorities. Staff rank the open items, see the combined ranking, and product
 * manages the list. Who sees which tab comes from GET /me (sf-api decides; this only hides tabs):
 *   My ranking         voters (an active admin app account with a team)
 *   Results            voters, manage and insights users
 *   Items              admin:priorities:manage
 *   People and weights admin:priorities:insights
 *
 * SF-5080: a list picker above the tabs chooses the list every tab shows (?list= in the URL).
 * Suggest is for everyone here; Lists is for manage users. SF-5087: with no ?list=, the page
 * shows the list GET /me answers for (the oldest list open for voting); no list is special.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermissions } from '@refinedev/core';
import { hasPermission } from '../../utils/permissions';
import { Card, Result, Skeleton, Tabs, Typography } from 'antd';
import type { TabsProps } from 'antd';
import {
  prioritiesErrorCode,
  usePrioritiesMe,
  usePriorityLists,
  type PrioritiesMe,
} from './api/prioritiesClient';
import { MyRankingTab } from './components/MyRankingTab';
import { ResultsTab } from './components/ResultsTab';
import { ItemsTab } from './components/ItemsTab';
import { PeopleWeightsTab } from './components/PeopleWeightsTab';
import { LoadError, RefreshWarning } from './components/LoadError';
import { ListPicker } from './components/ListPicker';
import { ListsTab } from './components/ListsTab';
import { SuggestTab } from './components/SuggestTab';

export const NO_TEAM_MESSAGE = 'You can rank priorities once an admin sets your team';

export const NO_LIST_MESSAGE = 'No list is open for voting right now';

/** sf-api's permission for the Items and Lists tabs (its MANAGE_PERMISSION). */
export const MANAGE_PERMISSION = 'admin:priorities:manage';

export const PrioritiesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const asked = searchParams.get('list') || undefined;
  const meQuery = usePrioritiesMe(asked);
  const listsQuery = usePriorityLists();
  // The sign-in's own permissions, for when /me cannot answer (below). sf-api still checks
  // every Lists call; this only decides whether the tab shows.
  const { data: permissions } = usePermissions<string[]>();
  const lists = listsQuery.data?.data?.lists ?? [];
  // Kept here, so a list switch (which reloads /me) does not send the person back to the first tab.
  const [activeTab, setActiveTab] = useState<string>();

  // Who the person is (voter, manage, insights, N) does not depend on the list, so while /me
  // for a newly picked list loads, the tabs stay up with the last answer: a typed suggestion
  // survives the switch. Everything list-scoped reads its own list (and shows its own loading).
  const lastMe = useRef<PrioritiesMe>();
  if (meQuery.data?.data) lastMe.current = meQuery.data.data;
  const me = meQuery.data?.data ?? (meQuery.isLoading ? lastMe.current : undefined);
  // The list shown: the one asked for, else the one sf-api picked for no list.
  const listId = asked ?? meQuery.data?.data?.list?.id ?? '';

  // The list asked for is not one this person can use: hidden from voters (sf-api answers
  // LIST_NOT_FOUND, as it does for one that is gone), or missing from the lists they get. The
  // first list open for voting is picked instead; with none, the page says so, picker and all.
  // With no list asked for, a 404 means no list is open for voting at all.
  const unavailable =
    prioritiesErrorCode(meQuery.error) === 'LIST_NOT_FOUND' ||
    (!!listId && lists.length > 0 && !lists.some((l) => l.id === listId));
  const firstVoting = lists.find((l) => l.status === 'voting')?.id;
  const replacement = unavailable && firstVoting !== listId ? firstVoting : undefined;
  useEffect(() => {
    if (!replacement) return;
    // Replacing (not pushing) the URL: Back skips the list that could not be shown.
    setSearchParams({ list: replacement }, { replace: true });
  }, [replacement, setSearchParams]);

  // Leaving a list with an unsaved ranking goes through Refine's unsaved-changes guard (the
  // UnsavedChangesNotifier in App.tsx asks before any navigation, this one included). It clears
  // the unsaved flag only once the person agrees, and a refusal leaves the page, the draft and
  // the guard as they were.
  const switchList = (next: string) => setSearchParams({ list: next });

  const picker = (
    <>
      <ListPicker
        lists={lists}
        value={listId}
        loading={listsQuery.isLoading}
        onChange={switchList}
      />
      {listsQuery.isError && (
        <RefreshWarning
          error={listsQuery.error}
          fallback="The lists could not be loaded."
          onRetry={listsQuery.refetch}
        />
      )}
    </>
  );
  const shell = (body: React.ReactNode) => (
    <Card>
      <Typography.Title level={3}>Product priorities</Typography.Title>
      {picker}
      {body}
    </Card>
  );

  if (unavailable) {
    // Waiting for the lists, or for the switch to the first one open for voting.
    if (listsQuery.isLoading || replacement) return shell(<Skeleton active />);
    const none = (
      <Result
        status="info"
        title={NO_LIST_MESSAGE}
        subTitle={lists.length > 0 ? 'Pick a list above to see it.' : undefined}
      />
    );
    // SF-5087: with no list open for voting (the last one deleted, say), /me answers 404 for no
    // list, so it says nothing about the person. Permissions do not depend on the list: the last
    // /me answer, else the sign-in's permissions, decide. A manager keeps the Lists tab, to add a
    // list and start over; anyone else sees only the message.
    const canManage =
      (me ?? lastMe.current)?.canManage ?? hasPermission(permissions ?? [], MANAGE_PERMISSION);
    if (!canManage) return shell(none);
    return shell(
      <>
        {none}
        <Tabs
          activeKey="lists"
          items={[{ key: 'lists', label: 'Lists', children: <ListsTab /> }]}
        />
      </>,
    );
  }
  if ((!me || !listId) && meQuery.isLoading) {
    return (
      <Card>
        <Skeleton active />
      </Card>
    );
  }
  // The full error only when nothing was ever loaded. A failed refetch (every write refreshes
  // /me) keeps the tabs and any unsaved ranking, with a warning above them.
  if (!me) {
    return (
      <LoadError
        error={meQuery.error}
        fallback="Priorities could not be loaded."
        onRetry={meQuery.refetch}
      />
    );
  }
  if (!me.isVoter && !me.canManage && !me.canInsights) {
    return <Result status="info" title={NO_TEAM_MESSAGE} />;
  }

  // The picked list's status from the lists (the freshest answer), else from /me.
  const listStatus =
    lists.find((l) => l.id === listId)?.status ??
    (me.list?.id === listId ? me.list?.status : undefined);

  // The other tabs unmount when left, so they read fresh data when shown again. My ranking stays
  // mounted once opened: its unsaved draft survives a tab switch, and its unsaved-changes warning
  // (Refine's warnWhenUnsavedChanges) still guards leaving the page from any tab.
  const tabs: TabsProps['items'] = [
    me.isVoter && {
      key: 'ranking',
      label: 'My ranking',
      // Keyed by list: another list's ranking starts from its own saved order.
      children: (
        <MyRankingTab key={listId} topN={me.topN} listId={listId} listStatus={listStatus} />
      ),
    },
    {
      key: 'results',
      label: 'Results',
      destroyInactiveTabPane: true,
      children: <ResultsTab canInsights={me.canInsights} listId={listId} />,
    },
    {
      key: 'suggest',
      label: 'Suggest',
      destroyInactiveTabPane: true,
      children: <SuggestTab lists={lists} listId={listId} />,
    },
    me.canManage && {
      key: 'items',
      label: 'Items',
      destroyInactiveTabPane: true,
      // Keyed by list: an edit, accept, fold or Eva window open on one list never stays open
      // on another (where saving would change the first list's item or suggestion).
      children: <ItemsTab key={listId} topN={me.topN} listId={listId} lists={lists} />,
    },
    me.canManage && {
      key: 'lists',
      label: 'Lists',
      destroyInactiveTabPane: true,
      children: <ListsTab />,
    },
    me.canInsights && {
      key: 'people',
      label: 'People and weights',
      destroyInactiveTabPane: true,
      children: <PeopleWeightsTab listId={listId} />,
    },
  ].filter((tab): tab is NonNullable<typeof tab> & object => !!tab);

  return (
    <Card>
      <Typography.Title level={3}>Product priorities</Typography.Title>
      {meQuery.isError && (
        <RefreshWarning
          error={meQuery.error}
          fallback="Priorities could not be refreshed."
          onRetry={meQuery.refetch}
        />
      )}
      {picker}
      <Tabs items={tabs} activeKey={activeTab} onChange={setActiveTab} />
    </Card>
  );
};
